// The existing admin-only Oppgaver endpoint is read-only. Unknown data never imply submission.
const orgKey = value => String(value ?? '').replace(/\s/g, '');
import companyIdentity from '../../shared/company-identity.cjs';
const nameKey = value => String(value ?? '').normalize('NFC').trim().toLocaleLowerCase('nb-NO').replace(/\s+/g, ' ');
const statusValues = new Set(['sent', 'not_sent', 'not_applicable', 'paused', 'error', 'unknown', 'unavailable']);
export const hasVatOrg = value => /^\d{9}$/.test(orgKey(value));
const validPeriod = period => Number.isInteger(Number(period?.year)) && Number(period.year) >= 2000 && Number(period.year) <= 2100
    && Number.isInteger(Number(period.monthTo)) && Number(period.monthTo) >= 1 && Number(period.monthTo) <= 12;
const unavailable = reason => ({ statuses: {}, fallback: { status: 'unavailable', reason } });

export async function loadVatSource(fetchFn, userRole, period) {
    if (userRole !== 'admin') return unavailable('admin_required');
    if (!validPeriod(period)) return unavailable('invalid_period');
    try {
        const response = await fetchFn('/api/oppgaver-mva?year=' + Number(period.year) + '&monthTo=' + Number(period.monthTo));
        if (!response.ok) return unavailable(response.status === 403 ? 'admin_required' : 'read_failed');
        const source = await response.json();
        if (!source || typeof source.statuses !== 'object' || Array.isArray(source.statuses)) return unavailable('invalid_response');
        return source;
    } catch { return unavailable('read_failed'); }
}

export function vatSubmissionFor(source, orgNr, period, companyName = '', identity) {
    const year = validPeriod(period) ? Number(period.year) : null;
    const base = { orgNr: orgKey(orgNr), year, source: 'Oppgaver', checkedAt: null };
    const missing = (status, reason) => ({
        ...base, status, reason,
        terms: Array.from({ length: 6 }, (_, index) => ({ term: index + 1, year, status, reason })),
    });
    if (!hasVatOrg(base.orgNr)) return missing('unknown', 'identity_unverified');
    if (year === null) return missing('unknown', 'invalid_period');
    const selected = source?.statuses?.[base.orgNr];
    if (!selected) return missing(source?.fallback?.status === 'unavailable' ? 'unavailable' : 'unknown', source?.fallback?.reason || 'identity_unverified');
    if (selected.status === 'unavailable') return missing('unavailable', selected.reason || 'read_failed');
    if (selected.reason) return missing('unknown', selected.reason);
    const binding = identity || selected.identity;
    const alias = companyIdentity.verifiedBinding(binding,base.orgNr,selected.sourceId)
        && companyIdentity.verifiedBinding(selected.identity,base.orgNr,selected.sourceId)
        && binding.sourceIdentity===selected.identity.sourceIdentity;
    if (selected.source !== 'Oppgaver' || selected.orgNr !== base.orgNr
        || selected.year !== year || (binding && !alias) || (companyName && nameKey(selected.companyName) !== nameKey(companyName) && !alias)) return missing('unknown', 'identity_unverified');
    if (!Array.isArray(selected.terms) || selected.terms.length !== 6
        || selected.terms.some((row, index) => row?.term !== index + 1 || row.year !== year || !statusValues.has(row.status))) return missing('unknown', 'invalid_terms');
    return {
        ...base, status: 'ok', checkedAt: selected.checkedAt || null, ...(alias ? {identity:binding} : {}),
        terms: selected.terms.map(row => ({ term: row.term, year, status: row.status, ...(typeof row.rawStatus === 'string' ? { rawStatus: row.rawStatus } : {}) })),
    };
}
